import React from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/editor-common/analytics';
import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import { makeCloseRemixModal } from './editor-commands/close-remix-modal';
import { makeOpenRemixModal } from './editor-commands/open-remix-modal';
import { replaceMauiApp } from './editor-commands/replace-maui-app';
import { makeStartRemixThinking } from './editor-commands/start-remix-thinking';
import { stopRemixThinking } from './editor-commands/stop-remix-thinking';
import type { MalleableUiPlugin, MalleableUiSharedState } from './malleableUiPluginType';
import { remixButtonDecoration } from './pm-plugins/decorations-remix-button';
import {
	createMauiDiagramPastePmPlugin,
	mauiDiagramPastePluginKey,
	type MauiDiagramPasteConfig,
} from './pm-plugins/maui-diagram-paste-plugin';
import { createMalleableUiPmPlugin, malleableUiPluginKey } from './pm-plugins/plugin-key';
import {
	createRemixThinkingPmPlugin,
	remixThinkingPluginKey,
} from './pm-plugins/remix-thinking-plugin';
import { MauiDiagramPasteFlag } from './ui/components/MauiDiagramPasteFlag';
import { PersistedSelectionHighlightStyles } from './ui/components/PersistedSelectionHighlightStyles';
import { RemixContent } from './ui/components/RemixContent';
import { shouldShowRemixForSelectionContext } from './ui/remix-config';
import { getOverriddenFloatingToolbarConfig } from './ui/toolbars/overridden-floating-toolbar-config';
import { getSelectionToolbarComponents } from './ui/toolbars/toolbar-components';

const REMIX_BUTTON_DECORATION_TYPE = 'REMIX_BUTTON';

export const malleableUiPlugin: MalleableUiPlugin = ({ api, config }) => {
	if (api && expValEqualsNoExposure('confluence_remix_icon_right_side', 'isEnabled', true)) {
		const remixDisabledReason = config?.__remixDisabledReason;
		const isRemixDisabled = !!remixDisabledReason;
		api.toolbar?.actions.registerComponents(
			getSelectionToolbarComponents(api, { remixDisabledReason, isDisabled: isRemixDisabled }),
		);
		api.blockControls.actions.registerNodeDecoration({
			type: REMIX_BUTTON_DECORATION_TYPE,
			create: (params) =>
				remixButtonDecoration({
					...params,
					api,
					livePage: config?.__livePage,
					remixDisabledReason,
					isDisabled: isRemixDisabled,
				}),
			showInViewMode: true,
			shouldCreate: (params) => {
				const { editorState, rootPos } = params;
				if (rootPos === undefined) {
					return false;
				}
				const node = editorState.doc.nodeAt(rootPos);
				return shouldShowRemixForSelectionContext({ node: node ?? undefined });
			},
		});
	}

	return {
		name: 'malleableUi',

		pmPlugins() {
			const plugins = [
				{
					name: 'malleableUi',
					plugin: ({ dispatch }: { dispatch: Dispatch }) => createMalleableUiPmPlugin(dispatch),
				},
				{
					name: 'remixThinking',
					plugin: ({ dispatch }: { dispatch: Dispatch }) => createRemixThinkingPmPlugin(dispatch),
				},
			];

			if (config?.diagramPaste) {
				const diagramPasteConfig: MauiDiagramPasteConfig = {
					...config.diagramPaste,
					fireAnalyticsEvent: (payload) => {
						api?.analytics?.actions.fireAnalyticsEvent(payload as AnalyticsEventPayload);
					},
				};
				plugins.push({
					name: 'mauiDiagramPaste',
					plugin: ({ dispatch }: { dispatch: Dispatch }) =>
						createMauiDiagramPastePmPlugin(dispatch, diagramPasteConfig),
				});
			}

			return plugins;
		},

		getSharedState(editorState): MalleableUiSharedState | undefined {
			if (!editorState) {
				return undefined;
			}
			const malleableUiState = malleableUiPluginKey.getState(editorState);
			const remixThinkingState = remixThinkingPluginKey.getState(editorState);
			if (!malleableUiState || !remixThinkingState) {
				return undefined;
			}
			return {
				isRemixModalOpen: malleableUiState.isRemixModalOpen,
				remixModalMetadata: malleableUiState.remixModalMetadata,
				isThinking: remixThinkingState.isThinking,
				remixSource: malleableUiState.remixSource,
				diagramPaste: mauiDiagramPastePluginKey.getState(editorState),
				savedSelection: remixThinkingState.savedSelection,
			};
		},

		commands: {
			openRemixModal: makeOpenRemixModal(api),
			closeRemixModal: makeCloseRemixModal(api),
			startRemixThinking: makeStartRemixThinking(api),
			stopRemixThinking,
			replaceMauiApp,
		},

		contentComponent: () => {
			return (
				<>
					<PersistedSelectionHighlightStyles />
					<RemixContent api={api} config={config} />
					{config?.diagramPaste && <MauiDiagramPasteFlag api={api} />}
				</>
			);
		},

		pluginsOptions: {
			floatingToolbar: config?.__remixDisabledReason
				? undefined
				: getOverriddenFloatingToolbarConfig(api),
		},
	};
};
